/**
 * Petty-cash specific formatters.
 *
 * The cross-cutting paise → INR helpers live in `apps/web/src/lib/format.ts`;
 * this file only owns the shapes that are unique to the petty-cash domain
 * (variance copy, outcome tone). Per §7 rule 17, anything reusable across
 * verticals stays in the cross-cutting `lib/format.ts`.
 */
import { paiseToInr } from "../../../lib/format.ts";

export type CountOutcome = "exact" | "short" | "over";

export interface VarianceFormat {
  /** Short label — `"exact"`, `"short ₹140"`, `"over ₹50"`. */
  text: string;
  outcome: CountOutcome;
}

/**
 * Translate a signed variance paise value (book − counted) into a
 * human-friendly label + outcome tag. The sign convention follows
 * `petty-cash.html` recent-counts row: positive = over (counter found
 * more than the book), negative = short.
 */
export function formatVariance(variancePaise: string | bigint): VarianceFormat {
  const paise = typeof variancePaise === "string" ? BigInt(variancePaise || "0") : variancePaise;
  if (paise === 0n) return { text: "exact", outcome: "exact" };
  const outcome: CountOutcome = paise > 0n ? "over" : "short";
  const abs = paise < 0n ? -paise : paise;
  return { text: `${outcome} ${paiseToInr(abs)}`, outcome };
}

/** Tailwind classes for the outcome chip (matches `prototype/cash/petty-cash.html` `.status-pill`). */
export const OUTCOME_CHIP_CLASS: Record<CountOutcome, string> = {
  exact: "bg-ok-50 text-ok border-ok-100",
  over: "bg-sky-50 text-sky-700 border-sky-100",
  short: "bg-warn-50 text-warn border-warn-100",
};
